/**
 * Placeholder geometry for panes whose contents are being replaced. Each one
 * mirrors the real element's box so the answer drops in without reflow — a
 * skeleton that jumps when the data lands is worse than no skeleton at all.
 *
 * These stand in for content the view is ABOUT to replace (a different mailbox,
 * a different filter). Content that is merely being revalidated stays on
 * screen under a `.comms-progress` bar instead.
 */

/** Deterministic widths — random ones re-roll on every render and twitch. */
const LINE_WIDTHS = ['58%', '82%', '44%', '70%', '64%', '88%', '52%', '76%'];

export function ConversationRowsSkeleton({ count = 8 }: { count?: number }) {
  return (
    <div aria-hidden>
      {Array.from({ length: count }, (_, index) => (
        <div className="cv-skrow" key={index}>
          <div className="cv-skrow-inner">
            <div className="sk cv-skrow-avatar" />
            <div className="cv-skrow-main">
              <div className="cv-skrow-band top">
                <div className="sk" style={{ flex: 1, maxWidth: LINE_WIDTHS[index % 8] }} />
                <div className="sk" style={{ width: 30 }} />
              </div>
              <div className="cv-skrow-band sub">
                <div className="sk" style={{ flex: 1, maxWidth: LINE_WIDTHS[(index + 3) % 8] }} />
              </div>
              <div className="cv-skrow-band snippet">
                <div className="sk" style={{ height: 9, width: '96%' }} />
                <div className="sk" style={{ height: 9, width: LINE_WIDTHS[(index + 5) % 8] }} />
              </div>
              <div className="cv-skrow-band meta">
                <div className="sk" style={{ width: 46 }} />
                <div style={{ flex: 1 }} />
                <div className="sk" style={{ width: 20 }} />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Rows for a `.ctable`. `columns` are flex weights matching the real header so
 * the placeholder cells line up under their own column titles.
 */
export function TableRowsSkeleton({
  columns,
  count = 6,
}: {
  columns: number[];
  count?: number;
}) {
  return (
    <div aria-hidden>
      {Array.from({ length: count }, (_, row) => (
        <div className="ctable-skrow" key={row}>
          {columns.map((weight, cell) => (
            <span
              className="sk"
              key={cell}
              style={{ flex: weight, maxWidth: `${55 + ((row * 7 + cell * 13) % 35)}%` }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Alternating in/out bubbles for a thread that is still loading. */
export function ThreadSkeleton() {
  const bubbles = [
    { out: false, w: 210, h: 44 },
    { out: true, w: 260, h: 62 },
    { out: false, w: 180, h: 38 },
    { out: true, w: 224, h: 44 },
  ];
  return (
    <div aria-hidden style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {bubbles.map((bubble, index) => (
        <div
          className="sk"
          key={index}
          style={{
            alignSelf: bubble.out ? 'flex-end' : 'flex-start',
            width: bubble.w,
            maxWidth: '80%',
            height: bubble.h,
            borderRadius: bubble.out ? '10px 10px 3px 10px' : '10px 10px 10px 3px',
          }}
        />
      ))}
    </div>
  );
}

/** Placeholder cards for the connected-channels grid. */
export function ChannelCardsSkeleton({ count = 3 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <div className="cset-card" key={index} aria-hidden>
          <div className="sk" style={{ width: 38, height: 38, borderRadius: 9, flex: '0 0 38px' }} />
          <div className="cset-card-main" style={{ display: 'grid', gap: 8 }}>
            <div className="sk" style={{ height: 11, width: '48%' }} />
            <div className="sk" style={{ height: 9, width: '72%' }} />
            <div className="sk" style={{ height: 9, width: '60%' }} />
          </div>
        </div>
      ))}
    </>
  );
}
